import React, { useState, useEffect } from 'react';
import { CharacterData } from '../../types/character';
import { useFirebaseCharacter } from '../../hooks/useFirebaseCharacter';
import Card from '../Common/Card';
import Button from '../Common/Button';
import CharacterCreationWizard, { CharacterCreationData } from '../CharacterCreation/CharacterCreationWizard';
import './CharacterSelect.css';

interface CharacterSelectProps {
  userId: string;
  onSelectCharacter: (characterId: string) => void;
  onCreateCharacter: (characterData?: CharacterCreationData) => Promise<string>;
}

const CharacterSelect: React.FC<CharacterSelectProps> = ({
  userId,
  onSelectCharacter,
  onCreateCharacter,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const firebase = useFirebaseCharacter({ userId });
  
  // Load user characters on mount
  useEffect(() => {
    if (userId) {
      firebase.loadUserCharacters().catch(console.error);
    }
  }, [firebase.loadUserCharacters, userId]);

  // Auto-create character if no characters exist (disabled for testing)
  // useEffect(() => {
  //   if (firebase.characters.length === 0 && !firebase.charactersLoading && userId) {
  //     handleCreateCharacter();
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [firebase.characters.length, firebase.charactersLoading, userId]);

  const handleCreateCharacter = async (characterData?: CharacterCreationData) => {
    if (isCreating) return;
    
    setIsCreating(true);
    try {
      const characterId = await onCreateCharacter(characterData);
      onSelectCharacter(characterId);
    } catch (error) {
      console.error('Failed to create character:', error);
    } finally {
      setIsCreating(false);
      setShowWizard(false);
    }
  };

  const handleShowWizard = () => {
    setShowWizard(true);
  };

  const handleCancelWizard = () => {
    setShowWizard(false);
  };

  const handleSelectExisting = (character: CharacterData) => {
    if (character.id) {
      onSelectCharacter(character.id);
    }
  };

  const handleDeleteCharacter = async (characterId: string, characterName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${characterName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await firebase.deleteCharacter(characterId);
    } catch (error) {
      console.error('Failed to delete character:', error);
      alert('Failed to delete character. Please try again.');
    }
  };

  // Show wizard if requested
  if (showWizard) {
    return (
      <CharacterCreationWizard
        onComplete={handleCreateCharacter}
        onCancel={handleCancelWizard}
      />
    );
  }

  // Firebase character management
  return (
    <div className="character-select">
      <Card title="Character Selection">
        <div className="character-select__content">
          
          {/* Loading state */}
          {firebase.charactersLoading && (
            <div className="character-select__loading">
              <p>Loading your characters...</p>
            </div>
          )}

          {/* Error state */}
          {firebase.error && (
            <div className="character-select__error">
              <p>Error loading characters: {firebase.error}</p>
              <Button
                variant="SECONDARY"
                onClick={() => firebase.loadUserCharacters()}
              >
                Retry
              </Button>
            </div>
          )}

          {/* Character list */}
          {!firebase.charactersLoading && !firebase.error && firebase.characters.length > 0 && (
            <div className="character-select__list">
              <div className="character-select__characters">
                {firebase.characters.map((character) => (
                  <div key={character.id} className="character-select__character">
                    <div className="character-card">
                      <div className="character-card__info">
                        <h4 className="character-card__name">
                          {character.name || 'Unnamed Character'}
                        </h4>
                        <p className="character-card__details">
                          Level {character.level} {character.class} {character.heritage && `(${character.heritage})`}
                        </p>
                        <p className="character-card__tier">
                          Tier {character.tier}
                        </p>
                      </div>
                      <div className="character-card__actions">
                        <Button
                          variant="PRIMARY"
                          size="SMALL"
                          onClick={() => handleSelectExisting(character)}
                        >
                          Select
                        </Button>
                        <Button
                          variant="CLEAR"
                          size="SMALL"
                          onClick={() => handleDeleteCharacter(character.id!, character.name)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create new character */}
          <div className="character-select__create">
            <Button
              variant="SECONDARY"
              onClick={handleShowWizard}
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create New Character'}
            </Button>
            <Button
              variant="CLEAR"
              onClick={() => handleCreateCharacter()}
              disabled={isCreating}
              size="SMALL"
            >
              Quick Create (Skip Wizard)
            </Button>
          </div>

          {/* Empty state */}
          {!firebase.charactersLoading && !firebase.error && firebase.characters.length === 0 && (
            <div className="character-select__empty">
              <p>You don't have any characters yet.</p>
              <p>Create your first enhanced character to get started!</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CharacterSelect;